import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import Modal from '../components/Modal';
import './Jogadores.css';

/**
 * Página de Jogadores com CRUD completo
 * Conforme doc 05-especificacoes-de-api.md
 */

const Jogadores = () => {
  const { isAdmin } = useAuth();
  const [jogadores, setJogadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal de criação
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [novoJogador, setNovoJogador] = useState({
    nome: '',
    email: '',
    criadoPorAdmin: true,
    senha: '',
  });

  // Modal de edição
  const [showEditModal, setShowEditModal] = useState(false);
  const [jogadorEditando, setJogadorEditando] = useState(null);


  const [mostrarInativados, setMostrarInativados] = useState(false);

  const carregarJogadores = async () => {
    try {
      setLoading(true);
      setError('');
      let response;
      if (mostrarInativados && isAdmin) {
        response = await apiClient.get('/jogadores/todos');
      } else {
        response = await apiClient.get('/jogadores');
      }
      
      // Verificar se a resposta é válida
      if (response && response.data) {
        setJogadores(Array.isArray(response.data) ? response.data : []);
      } else {
        setJogadores([]);
        setError('Nenhum jogador encontrado');
      }
    } catch (err) {
      console.error('Erro ao carregar jogadores:', err);
      setError(err.response?.data?.error || 'Erro ao carregar jogadores');
      setJogadores([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarJogadores();
  }, [mostrarInativados, isAdmin]);

  const handleReativar = async (id) => {
    if (!window.confirm('Deseja reativar este jogador?')) return;
    
    try {
      await apiClient.post(`/jogadores/${id}/reativar`);
      carregarJogadores();
    } catch (err) {
      console.error('Erro ao reativar jogador:', err);
      alert(err.response?.data?.error || 'Erro ao reativar jogador');
    }
  };

  const handleCriar = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/jogadores', novoJogador);
      setShowCreateModal(false);
      setNovoJogador({ nome: '', email: '', criadoPorAdmin: true, senha: '' });
      carregarJogadores();
    } catch (err) {
      console.error('Erro ao criar jogador:', err);
      alert(err.response?.data?.error || 'Erro ao criar jogador');
    }
  };

  const handleEditar = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        nome: jogadorEditando.nome,
        email: jogadorEditando.email,
        is_admin: jogadorEditando.is_admin,
        ativo: jogadorEditando.ativo,
      };
      // Só envia senha se foi preenchida
      if (jogadorEditando.senha && jogadorEditando.senha.trim() !== '') {
        payload.senha = jogadorEditando.senha;
      }
      
      await apiClient.put(`/jogadores/${jogadorEditando.id}`, payload);
      setShowEditModal(false);
      setJogadorEditando(null);
      carregarJogadores();
    } catch (err) {
      console.error('Erro ao editar jogador:', err);
      alert(err.response?.data?.error || 'Erro ao editar jogador');
    }
  };

  const [showResetModal, setShowResetModal] = useState(false);
  const [jogadorResetando, setJogadorResetando] = useState(null);
  const [novaSenha, setNovaSenha] = useState('');

  const handleResetSenha = (jogador) => {
    setJogadorResetando(jogador);
    setNovaSenha('');
    setShowResetModal(true);
  };

  const confirmarResetSenha = async () => {
    if (!jogadorResetando) return;

    try {
      const payload = novaSenha ? { nova_senha: novaSenha } : {};
      const response = await apiClient.post(`/jogadores/${jogadorResetando.id}/reset`, payload);
      
      alert(response.data.message);
      if (response.data.token_cadastro) {
        alert(`Token gerado: ${response.data.token_cadastro}\nLink: ${response.data.link_cadastro}`);
      }
      
      setShowResetModal(false);
      setJogadorResetando(null);
      setNovaSenha('');
      carregarJogadores();
    } catch (err) {
      console.error('Erro ao resetar senha:', err);
      alert(err.response?.data?.error || 'Erro ao resetar senha');
    }
  };

  const handleInativar = async (id) => {
    if (!window.confirm('Deseja inativar este jogador?')) return;
    
    try {
      await apiClient.delete(`/jogadores/${id}`);
      carregarJogadores();
    } catch (err) {
      console.error('Erro ao inativar jogador:', err);
      alert(err.response?.data?.error || 'Erro ao inativar jogador');
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>👥 Jogadores</h1>
        <div>
          {isAdmin && (
            <>
              <label style={{ marginRight: '15px' }}>
                <input
                  type="checkbox"
                  checked={mostrarInativados}
                  onChange={(e) => setMostrarInativados(e.target.checked)}
                />
                {' '}Mostrar inativados
              </label>
              <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                + Novo Jogador
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {jogadores.length === 0 ? (
        <div className="jogadores-empty">
          <p>Nenhum jogador encontrado</p>
        </div>
      ) : (
        <div className="jogadores-table-container">
          <table className="jogadores-table">
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Nome</th>
                <th>Email</th>
                <th>Status</th>
                <th>Tipo</th>
                {isAdmin && <th>Ações</th>}
              </tr>
            </thead>
            <tbody>
              {jogadores.map((jogador) => (
                <tr 
                  key={jogador.id} 
                  className={`jogador-row ${jogador.is_deleted ? 'inativo' : ''} ${jogador.is_admin ? 'admin' : ''}`}
                >
                  <td>
                    <div className="jogador-avatar-table">
                      {jogador.nome.charAt(0).toUpperCase()}
                    </div>
                  </td>
                  <td>
                    <div className="jogador-nome-table">{jogador.nome}</div>
                  </td>
                  <td>
                    <div className="jogador-email-table">{jogador.email}</div>
                  </td>
                  <td>
                    {jogador.is_deleted ? (
                      <span className="badge badge-danger">Inativado</span>
                    ) : (
                      <span className={`badge ${jogador.ativo ? 'badge-success' : 'badge-warning'}`}>
                        {jogador.ativo ? '✓ Ativo' : '⏳ Pendente'}
                      </span>
                    )}
                  </td>
                  <td>
                    {jogador.is_admin ? (
                      <span className="badge-admin-table">👑 Admin</span>
                    ) : (
                      <span className="badge-jogador-table">Jogador</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td>
                      <div className="jogador-actions-table">
                        {jogador.is_deleted ? (
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleReativar(jogador.id)}
                            title="Reativar jogador"
                          >
                            ↻ Reativar
                          </button>
                        ) : (
                          <>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setJogadorEditando(jogador);
                                setShowEditModal(true);
                              }}
                              title="Editar jogador"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => handleResetSenha(jogador)}
                              title="Resetar senha"
                            >
                              🔑
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleInativar(jogador.id)}
                              title="Inativar jogador"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Criação */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Novo Jogador"
      >
        <form onSubmit={handleCriar}>
          <div className="form-group">
            <label>Nome</label>
            <input
              type="text"
              value={novoJogador.nome}
              onChange={(e) => setNovoJogador({ ...novoJogador, nome: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={novoJogador.email}
              onChange={(e) => setNovoJogador({ ...novoJogador, email: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Senha (opcional)</label>
            <input
              type="password"
              value={novoJogador.senha}
              onChange={(e) => setNovoJogador({ ...novoJogador, senha: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={novoJogador.criadoPorAdmin}
                onChange={(e) => setNovoJogador({ ...novoJogador, criadoPorAdmin: e.target.checked })}
              />
              {' '}Criar como ativo (criado por admin)
            </label>
            <small className="form-help">
              Se desmarcar, será um auto-cadastro pendente de aprovação do admin.
            </small>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              Criar
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de Edição */}
      {jogadorEditando && (
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Editar Jogador"
        >
          <form onSubmit={handleEditar}>
            <div className="form-group">
              <label>Nome</label>
              <input
                type="text"
                value={jogadorEditando.nome}
                onChange={(e) => setJogadorEditando({ ...jogadorEditando, nome: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={jogadorEditando.email}
                onChange={(e) => setJogadorEditando({ ...jogadorEditando, email: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={jogadorEditando.is_admin}
                  onChange={(e) => setJogadorEditando({ ...jogadorEditando, is_admin: e.target.checked })}
                />
                {' '}Administrador
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={jogadorEditando.ativo}
                  onChange={(e) => setJogadorEditando({ ...jogadorEditando, ativo: e.target.checked })}
                />
                {' '}Ativo
              </label>
            </div>

            <div className="form-group">
              <label>Nova Senha (deixe em branco para não alterar)</label>
              <input
                type="password"
                value={jogadorEditando.senha || ''}
                onChange={(e) => setJogadorEditando({ ...jogadorEditando, senha: e.target.value })}
                placeholder="Deixe em branco para manter a senha atual"
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => setShowEditModal(false)}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                Salvar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal de Reset de Senha */}
      {jogadorResetando && (
        <Modal
          isOpen={showResetModal}
          onClose={() => {
            setShowResetModal(false);
            setJogadorResetando(null);
            setNovaSenha('');
          }}
          title="Reset de Senha"
        >
          <div>
            <p><strong>Jogador:</strong> {jogadorResetando.nome}</p>
            <p><strong>Email:</strong> {jogadorResetando.email}</p>
            
            <div className="form-group">
              <label>
                Nova Senha (opcional - deixe em branco para gerar token)
              </label>
              <input
                type="password"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                placeholder="Se preencher, a senha será definida diretamente e o jogador será ativado"
              />
              <small className="form-help">
                Se você preencher a senha, ela será definida diretamente e o jogador será ativado automaticamente.
                Se deixar em branco, será gerado um token para o jogador definir a senha depois.
              </small>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setShowResetModal(false);
                  setJogadorResetando(null);
                  setNovaSenha('');
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmarResetSenha}
              >
                Confirmar Reset
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Jogadores;
